import React, { Fragment } from 'react';
import { ModalX } from '../../../components';
import {
    Button,
    Checkbox,
    Chip,
    IconButton,
    Input,
    List,
    ListItem,
    Option,
    Radio,
    Select,
    Spinner,
    Step,
    Stepper,
    Textarea
} from '@material-tailwind/react';
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    BookOpenTextIcon,
    CalendarDays,
    ExternalLinkIcon,
    ListChecksIcon,
    PlusIcon,
    ScanQrCodeIcon,
    SearchIcon,
    SofaIcon,
    TrashIcon,
    UserIcon
} from 'lucide-react';
import {
    convertToPresentableText,
    formatNumberWithCommas,
    // getOrdinal,
    NotifyError,
    timeToDate
} from '../../../utils/functions';
import { calculateContractPayable, sumMonthlyRent, toMoney } from '../../../utils/finance';
import {
    CONNECTION_TYPES,
    CONNECTION_TYPES_ENUM,
    HOURLY_TIME_SLOTS_ENUM,
    OPERATIONAL_STATUSES,
    STATIC_USER_ROLES,
    VERIFICATION_TYPES_ENUM
} from '../../../utils/enums';
import { useGetOffices } from '../../../custom-hooks/use-offices';
import { useGetSeats } from '../../../custom-hooks/use-seats';
import { useAddContract } from '../../../custom-hooks/use-contracts';
import { useGetUsers } from '../../../custom-hooks/use-users';
import useDebounce from '../../../custom-hooks/useDebounce';
import { GetAvatarAndTextColor } from '../../../utils/ui-interpreters';
import moment from 'moment';
import { Link } from 'react-router-dom';

const contractModalTabs = [
    {
        label: 'Customer',
        description: 'Select Customer',
        value: 'customer',
        icon: <UserIcon size={16} />,
    },
    {
        label: 'General',
        description: 'General Details',
        value: 'general',
        icon: <BookOpenTextIcon size={16} />,
    },
    {
        label: 'Offices',
        description: 'Select Offices',
        value: 'offices',
        icon: <ScanQrCodeIcon size={16} />,
    },
    {
        label: 'Seats',
        description: 'Select Seats',
        value: 'seats',
        icon: <SofaIcon size={16} />,
    },
    {
        label: 'Payments',
        description: 'Add Payments',
        value: 'payment',
        icon: <CalendarDays size={16} />,
    },
    {
        label: 'Summary',
        description: 'Review Details',
        value: 'summary',
        icon: <ListChecksIcon size={16} />,
    }
];


const ContractFormModal = ({
    isOpen,
    onClose,
    isUpdating,
    defaultData,
    customer,
}) => {

    const [formData, setFormData] = React.useState(defaultData);
    const [loadingAddContract, setLoadingAddContract] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState(''); // Search term for offices and seats
    const [searchCustomerTerm, setSearchCustomerTerm] = React.useState(''); // Search term for customers

    const debouncedSearchCustomerTerm = useDebounce(searchCustomerTerm, 500);

    const { data: rawOffices, isLoading: isLoadingOffices } = useGetOffices();
    const { data: seats, isLoading: isLoadingSeats } = useGetSeats();

    const findAvailableOffices = (offices, newStartDate, newEndDate) => {
        // If no start date provided, return all offices (no filtering)
        if (!newStartDate) {
            return offices;
        }

        return offices.filter(office => {
            // Check if any existing booking conflicts with the new booking
            const hasConflict = office.bookings ? (office.bookings.length > 0 && office.bookings.some((booking) => {
                const existingStartMoment = moment(booking.startDate);
                const existingEndMoment = booking.endDate ? moment(booking.endDate) : null;
                const newStartMoment = moment(newStartDate);
                const newEndMoment = newEndDate ? moment(newEndDate) : null;

                // Two ranges overlap if:
                // 1. Existing starts before new ends (or new has no end)
                // 2. New starts before existing ends (or existing has no end)
                const existingStartsBeforeNewEnds = !newEndMoment || existingStartMoment.isBefore(newEndMoment);
                const newStartsBeforeExistingEnds = !existingEndMoment || newStartMoment.isBefore(existingEndMoment);

                return existingStartsBeforeNewEnds && newStartsBeforeExistingEnds;
            })) : false;

            return !hasConflict;
        });
    }

    const offices = findAvailableOffices(rawOffices, formData.startDate, formData.endDate);

    const addContractMutation = useAddContract();

    const { data: users, isLoading: isLoadingUsers } = useGetUsers({
        role: [STATIC_USER_ROLES.USER],
        _search: debouncedSearchCustomerTerm,
        _limit: 1000
    });

    const [activeStep, setActiveStep] = React.useState(customer ? 1 : 0);
    const [isLastStep, setIsLastStep] = React.useState(false);
    const [isFirstStep, setIsFirstStep] = React.useState(false);

    const handleNext = () => !isLastStep && setActiveStep((cur) => cur + 1);
    const handlePrev = () => !isFirstStep && setActiveStep((cur) => cur - 1);

    const availableTabs = contractModalTabs.filter(tab => {
        if (formData.contractType === CONNECTION_TYPES.DAY_PASS) {
            return !['seats', 'offices'].includes(tab.value);
        }
        else {
            return !['dayPass'].includes(tab.value);
        }
    })

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => {
            // If not nested, just update directly
            if (!name.includes('.')) {
                return {
                    ...prev,
                    [name]: value,
                };
            }
            // For deep nested keys, use a recursive helper
            const setDeepValue = (obj, keys, val) => {
                if (keys.length === 1) {
                    return { ...obj, [keys[0]]: val };
                }
                return {
                    ...obj,
                    [keys[0]]: setDeepValue(
                        obj && obj[keys[0]] ? obj[keys[0]] : {},
                        keys.slice(1),
                        val
                    ),
                };
            };
            const keys = name.split('.');
            return setDeepValue(prev, keys, value);
        });
    };

    // Generic handler for nested key value pairs (supports dot notation)
    const handleSelectChange = (name, value) => {
        setFormData((prev) => {
            // If not nested, just update directly
            if (!name.includes('.')) {
                return {
                    ...prev,
                    [name]: value,
                };
            }
            // For deep nested keys, use a recursive helper
            const setDeepValue = (obj, keys, val) => {
                if (keys.length === 1) {
                    return { ...obj, [keys[0]]: val };
                }
                return {
                    ...obj,
                    [keys[0]]: setDeepValue(
                        obj && obj[keys[0]] ? obj[keys[0]] : {},
                        keys.slice(1),
                        val
                    ),
                };
            };
            const keys = name.split('.');
            return setDeepValue(prev, keys, value);
        });
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => {
            // If not nested, just update directly
            if (!name.includes('.')) {
                return {
                    ...prev,
                    [name]: checked,
                };
            }
            // For deep nested keys, use a recursive helper
            const setDeepValue = (obj, keys, val) => {
                if (keys.length === 1) {
                    return { ...obj, [keys[0]]: val };
                }
                return {
                    ...obj,
                    [keys[0]]: setDeepValue(
                        obj && obj[keys[0]] ? obj[keys[0]] : {},
                        keys.slice(1),
                        val
                    ),
                };
            };
            const keys = name.split('.');
            return setDeepValue(prev, keys, checked);
        });
    };

    const handleOfficeSelection = (officeId) => {
        setFormData((prev) => {
            const isSelected = prev.selectedOffices.some(office => office.id === officeId);

            if (isSelected) {
                return {
                    ...prev,
                    selectedOffices: prev.selectedOffices.filter(office => office.id !== officeId)
                };
            } else {
                const selectedOfficeData = offices.find(o => o._id === officeId);
                return {
                    ...prev,
                    selectedOffices: [...prev.selectedOffices, {
                        id: officeId,
                        name: `Office # ${selectedOfficeData.number} (${selectedOfficeData.code})`, // For Read only purposes
                        isFullTime: !selectedOfficeData.isPartiallyAssigned,
                        startTime: '09:00',
                        endTime: '18:00',
                        monthlyRent: '',
                        startDate: formData.startDate,
                        endDate: formData.endDate,
                        // rentFrequency: { type: String, enum: ['once', 'daily', 'weekly', 'monthly', 'yearly'], default: 'monthly' },
                        rate: selectedOfficeData?.monthlyRate || 0,
                        comment: ''
                    }]
                };
            }
        });
    };

    const handleSeatSelection = (seatId) => {
        setFormData((prev) => {
            const isSelected = prev.selectedSeats.some(seat => seat.id === seatId);

            if (isSelected) {
                return {
                    ...prev,
                    selectedSeats: prev.selectedSeats.filter(seat => seat.id !== seatId)
                };
            } else {
                const selectedSeatData = seats.find(s => s._id === seatId);
                return {
                    ...prev,
                    selectedSeats: [...prev.selectedSeats, {
                        id: seatId,
                        name: `Seat # ${selectedSeatData.seatCode} - Space ${selectedSeatData.coworking.number} (${selectedSeatData.coworking.code})`, // For Read only purposes
                        isFullTime: !selectedSeatData.isPartiallyAssigned,
                        startTime: '09:00',
                        endTime: '18:00',
                        monthlyRent: '',
                        startDate: formData.startDate,
                        endDate: formData.endDate,
                        // rentFrequency: { type: String, enum: ['once', 'daily', 'weekly', 'monthly', 'yearly'], default: 'monthly' },
                        rate: selectedSeatData?.ratePerMonth || 0,
                        comment: ''
                    }]
                };
            }
        });
    };

    const handleOfficeDetailChange = (officeId, field, value) => {
        setFormData((prev) => ({
            ...prev,
            selectedOffices: prev.selectedOffices.map(office =>
                office.id === officeId ? { ...office, [field]: value } : office
            )
        }));
    };

    const handleSeatDetailChange = (seatId, field, value) => {
        setFormData((prev) => ({
            ...prev,
            selectedSeats: prev.selectedSeats.map(seat =>
                seat.id === seatId ? { ...seat, [field]: value } : seat
            )
        }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (isUpdating) {
            // Update contract logic
            // const updatedContracts = contracts.map((contract) =>
            //     contract._id === selectedContractId ? { ...contract, ...formData } : contract
            // );
            // setContracts(updatedContracts);
        } else {
            try {
                setLoadingAddContract(true);
                // Add new contract logic
                const newContract = {
                    user: customer ? customer._id : formData.user._id,
                    contractType: formData.contractType,
                    startDate: formData.startDate,
                    endDate: formData.endDate,
                    status: formData.status,
                    comment: formData.comment,
                    reason: formData.reason,
                    duration: formData.duration,
                    noticePeriod: formData.noticePeriod,
                    totalAdvanceRent: toMoney(formData.totalAdvanceRent),
                    oneTimeServiceCharges: toMoney(formData.oneTimeServiceCharges),
                    oneTimeOtherCharges: formData.oneTimeOtherCharges,
                    securityDeposit: toMoney(formData.securityDeposit),
                    bookings: [
                        ...formData.selectedOffices.map(office => ({
                            office: office.id,
                            dailyStartTime: !office.isFullTime ? timeToDate(office.startTime) : null,
                            dailyEndTime: !office.isFullTime ? timeToDate(office.endTime) : null,
                            rentRate: toMoney(office.rate),
                            rentTotal: toMoney(office.monthlyRent),
                            startDate: office.startDate,
                            endDate: office.endDate,
                            comment: office.comment
                        })),
                        ...formData.selectedSeats.map(seat => ({
                            seat: seat.id,
                            dailyStartTime: !seat.isFullTime ? timeToDate(seat.startTime) : null,
                            dailyEndTime: !seat.isFullTime ? timeToDate(seat.endTime) : null,
                            rentRate: toMoney(seat.rate),
                            rentTotal: toMoney(seat.monthlyRent),
                            startDate: seat.startDate,
                            endDate: seat.endDate,
                            comment: seat.comment
                        }))
                    ],
                };
                await addContractMutation.mutateAsync(newContract);
                onClose();
                setFormData(defaultData);
            } catch (error) {
                NotifyError(error.response.data.message, error);
            } finally {
                setLoadingAddContract(false);
            }
        }

    };

    const filteredOffices = offices?.filter(office =>
        office.code.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const filteredSeats = seats?.filter(seat =>
        seat.seatCode.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const calculateSummary = (offices, seats) => {
        if (formData.contractType === CONNECTION_TYPES.DAY_PASS) {
            const totalRent = toMoney(formData.dayPass.totalPrice);
            const exceptedAdvanceRent = totalRent * 2;
            const advanceRent = toMoney(formData.totalAdvanceRent);
            const securityDeposit = toMoney(formData.securityDeposit);
            const oneTimeServiceCharges = toMoney(formData.oneTimeServiceCharges);
            const totalPayable = calculateContractPayable({
                totalRent,
                advanceRent,
                securityDeposit,
                oneTimeServiceCharges,
                oneTimeOtherCharges: formData.oneTimeOtherCharges,
            });
            return {
                totalOffices: 0,
                totalSeats: 0,
                totalBookings: 0,
                exceptedAdvanceRent,
                advanceRent,
                securityDeposit,
                oneTimeServiceCharges,
                oneTimeOtherCharges: formData.oneTimeOtherCharges || [],
                totalRent,
                totalPayable,
                firstMonthPayable: totalPayable,
                rentType: formData.dayPass.priceType,
            }
        }

        const totalRent = sumMonthlyRent(offices, seats);
        const exceptedAdvanceRent = totalRent * 2;
        const advanceRent = toMoney(formData.totalAdvanceRent);
        const securityDeposit = toMoney(formData.securityDeposit);
        const oneTimeServiceCharges = toMoney(formData.oneTimeServiceCharges);
        const totalPayable = calculateContractPayable({
            totalRent,
            advanceRent,
            securityDeposit,
            oneTimeServiceCharges,
            oneTimeOtherCharges: formData.oneTimeOtherCharges,
        });

        return {
            totalOffices: offices.length,
            totalSeats: seats.length,
            totalBookings: offices.length + seats.length,
            exceptedAdvanceRent,
            advanceRent,
            securityDeposit,
            oneTimeServiceCharges,
            oneTimeOtherCharges: formData.oneTimeOtherCharges || [],
            totalRent,
            totalPayable,
            firstMonthPayable: totalPayable,
            rentType: 'monthly'
        }
    }

    const summary = calculateSummary(formData.selectedOffices, formData.selectedSeats);

    const handleSelectCustomer = (user) => {
        setFormData((prev) => ({
            ...prev,
            user: user
        }));
    }

    const renderCustomerSelection = () => customer ? (
        <div className="h-[390px] flex justify-center items-center">
            <div className="px-1 md:px-4">
                <div className="flex flex-col gap-2 items-center">
                    <div className={`w-24 h-24 rounded-full bg-gray-200 border flex items-center justify-center`}>
                        {
                            customer.name.split(' ').map((name, index) => (
                                <span key={index} className={`text-2xl font-bold capitalize`}>
                                    {name.charAt(0)}
                                </span>
                            ))
                        }
                    </div>
                    <span className="text-sm font-medium">{convertToPresentableText(customer.name)}</span>
                    <span className="text-xs text-gray-600">{customer.email}</span>
                    <span className="text-xs text-gray-600">{customer.phone}</span>
                </div>
                <hr className='my-4' />
                <div className="">
                    <Button onClick={handleNext} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                        Next Step <ArrowRightIcon className='w-4 h-4' /> {availableTabs[activeStep + 1].label}
                    </Button>
                </div>
            </div>

        </div>
    ) : (
        <div className="space-y-4">
            <div className="px-1 md:px-4">
                <Input
                    label="Search Customer"
                    placeholder="Search by name, email or phone"
                    value={searchCustomerTerm}
                    onChange={(e) => setSearchCustomerTerm(e.target.value)}
                    icon={<SearchIcon className="h-4 w-4" />}
                />
                <span className="text-xs text-gray-600">
                    Type the name, email or phone number to search
                </span>
            </div>
            <div className="h-[390px] overflow-y-auto">
                {
                    isLoadingUsers ? (
                        <div className="flex justify-center items-center h-full">
                            <Spinner className="w-4 h-4" />
                        </div>
                    ) : (
                        users?.users.length > 0 ? (
                            <List>
                                {
                                    users?.users.map(user => {
                                        const { bg, text } = GetAvatarAndTextColor(user.name);
                                        return (
                                            <ListItem key={user._id} selected={formData.user?._id === user._id} onClick={() => handleSelectCustomer(user._id === formData.user?._id ? null : user)}>
                                                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 w-full ">
                                                    <div className="flex items-center gap-2">
                                                        <div className="hidden md:block">
                                                            <Checkbox
                                                                checked={formData.user?._id === user._id}
                                                                className="h-4 w-4"
                                                                ripple={false}
                                                                readOnly
                                                            />
                                                        </div>
                                                        <div className={`w-10 h-10 rounded-full ${bg} border flex items-center justify-center`}>
                                                            {
                                                                user.name.split(' ').map((name, index) => (
                                                                    <span key={index} className={`text-xs font-bold ${text} capitalize`}>
                                                                        {name.charAt(0)}
                                                                    </span>
                                                                ))
                                                            }
                                                        </div>
                                                        <div className="flex flex-col gap-1">
                                                            <span className="text-sm font-medium">{convertToPresentableText(user.name)}</span>
                                                            <span className="text-xs text-gray-600">{user.email}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-1 items-start md:items-end">
                                                        <span className="text-xs text-gray-600 hidden md:block">{user.phone}</span>
                                                        <span className="text-xs text-gray-600">Member since {moment(user.createdDate).fromNow()}</span>
                                                    </div>
                                                </div>
                                            </ListItem>
                                        )
                                    })
                                }
                            </List>
                        ) : (
                            <div className="flex justify-center items-center h-full">
                                <span className="text-sm text-gray-600">No customers found</span>
                            </div>
                        )
                    )
                }

            </div>
            <hr />
            <div className="flex justify-end">
                {
                    <Button disabled={!formData.user} onClick={handleNext} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                        Next Step <ArrowRightIcon className='w-4 h-4' /> {availableTabs[activeStep + 1].label}
                    </Button>
                }
            </div>
        </div>
    )

    const renderGeneralDetails = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                    label="Contract Type"
                    value={formData.contractType}

                    onChange={(value) => handleSelectChange("contractType", value)}
                >
                    {
                        CONNECTION_TYPES_ENUM.map((value) => (
                            <Option key={value} value={value}>
                                {convertToPresentableText(value)}
                            </Option>
                        ))
                    }
                </Select>

                <Select
                    label="Status"
                    value={formData.status}
                    onChange={(value) => handleSelectChange("status", value)}
                >
                    {
                        VERIFICATION_TYPES_ENUM.map((value) => (
                            <Option key={value} value={value}>
                                {convertToPresentableText(value)}
                            </Option>
                        ))
                    }
                </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Input
                    type="date"
                    label="Start Date"
                    name="startDate"
                    required
                    value={formData.startDate}
                    onChange={handleInputChange}
                />
                <Input
                    type="date"
                    label="End Date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                />
                <Input
                    type="number"
                    label="Notice Period (weeks)"
                    name="noticePeriod"
                    value={formData.noticePeriod}
                    onChange={handleInputChange}
                />
            </div>
            <div className="">
                <Input
                    label="Purpose / Reason"
                    name="reason"
                    required
                    value={formData.reason}
                    onChange={handleInputChange}
                />
                <div className="flex items-center flex-wrap gap-1 mt-1">
                    <span className="text-xs text-gray-600">Examples:</span>
                    {
                        [
                            'Freelancer',
                            'Startup',
                            'Business',
                            'Corporate',
                            'Government',
                            'Education',
                            'Other',
                        ].map((value) => (
                            <Chip
                                key={value}
                                value={`#${value}`}
                                color="blue"
                                variant="outlined"
                                className='cursor-pointer hover:bg-gray-100 hover:text-gray-800 duration-300 rounded-full'
                                size='sm'
                                onClick={() => setFormData({ ...formData, reason: value })}
                            />
                        ))
                    }
                </div>
            </div>

            <Textarea
                label="Additional Comments"
                name="comment"
                value={formData.comment}
                onChange={handleInputChange}
            />
            <hr />
            <div className="flex justify-end gap-2">
                <Button onClick={handlePrev} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    {availableTabs[activeStep - 1].label}<ArrowLeftIcon className='w-4 h-4' /> Previous Step
                </Button>
                <Button disabled={!formData.startDate || !formData.reason} onClick={handleNext} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    Next Step <ArrowRightIcon className='w-4 h-4' /> {availableTabs[activeStep + 1].label}
                </Button>
            </div>
        </div>
    )

    const checkSelectedOfficesSpecifiedRent = formData.selectedOffices.filter(office => toMoney(office.monthlyRent) < 1000);
    const checkSelectedSeatsSpecifiedRent = formData.selectedSeats.filter(seat => toMoney(seat.monthlyRent) < 1000);

    const renderOfficeSelection = () => (
        <div className="mt-4 min-h-[480px]">
            <Input
                label="Search Offices"
                placeholder="Search by office code"
                icon={<SearchIcon className="h-4 w-4" />}
                value={searchTerm}
                disabled={isLoadingOffices}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="h-[390px] overflow-y-auto">
                {
                    isLoadingOffices ? (
                        <div className="flex justify-center items-center h-full">
                            <Spinner className="w-4 h-4" />
                        </div>
                    ) : (
                        <List>
                            {filteredOffices
                                .filter(office => office.status === OPERATIONAL_STATUSES.OPERATIONAL)
                                .slice() // Create a copy to avoid mutating the original array
                                .map((office) => {
                                    return (
                                        <Fragment key={office._id}>
                                            <ListItem
                                                className="py-1"
                                                // disabled={availability === 0}
                                                selected={formData.selectedOffices.some(o => o.id === office._id)}
                                                onClick={() => handleOfficeSelection(office._id)}>
                                                <div className="flex w-full items-center">
                                                    <div className="hidden md:block">
                                                        <Checkbox
                                                            checked={formData.selectedOffices.some(o => o.id === office._id)}
                                                            className="h-4 w-4"
                                                            ripple={false}
                                                            readOnly
                                                        />
                                                    </div>
                                                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center w-full">
                                                        <div className="font-thin">
                                                            Office # {office.number} ({office.code})
                                                            <div className={`text-xs`}>
                                                                <strong>{office.capacity}</strong> Seater | Rate: <strong>{office.monthlyRate}</strong> per month
                                                            </div>
                                                        </div>
                                                        {/* <div className="font-thin text-xs text-gray-600">
                                                            {availability === 0 ? "Not Available" : `Available ${availability}h`} <br />
                                                            On {getOrdinal(office.floor.number)} Floor, In {office.floor.building.name}, Branch {office.floor.building.branch.name}
                                                        </div> */}
                                                    </div>
                                                </div>

                                            </ListItem>
                                            {formData.selectedOffices.some(o => o.id === office._id) && (
                                                <div className="border-l-2 border-gray-400 ml-2 pl-2">
                                                    <div className={`grid grid-cols-1 md:grid-cols-4 gap-2 mb-2`}>
                                                        <div className="w-full">
                                                            <Input
                                                                type="number"
                                                                label="Monthly Rent"
                                                                placeholder={office.monthlyRate}
                                                                value={formData.selectedOffices.find(o => o.id === office._id)?.monthlyRent || ''}
                                                                onChange={(e) => handleOfficeDetailChange(office._id, 'monthlyRent', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="w-full col-span-3">
                                                            <Input
                                                                type="text"
                                                                label="Additional Comments"
                                                                value={formData.selectedOffices.find(o => o.id === office._id)?.comment || ''}
                                                                onChange={(e) => handleOfficeDetailChange(office._id, 'comment', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className={`grid grid-cols-1 md:grid-cols-3 gap-2 mb-2`}>
                                                        <div className={`flex flex-col md:flex-row gap-2 w-full`}>
                                                            <div className={`p-2 border rounded-lg w-full`}>
                                                                <span className="text-gray-600 text-sm font-bold">Booking Period</span>
                                                                <hr className="my-1" />
                                                                <div className={`mt-2`}>
                                                                    <Input
                                                                        type="date"
                                                                        label="Booking From"
                                                                        value={formData.selectedOffices.find(o => o.id === office._id)?.startDate || ''}
                                                                        onChange={(e) => handleOfficeDetailChange(office._id, 'startDate', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className={`mt-2`}>
                                                                    <Input
                                                                        type="date"
                                                                        label="Booking Till"
                                                                        value={formData.selectedOffices.find(o => o.id === office._id)?.endDate || ''}
                                                                        onChange={(e) => handleOfficeDetailChange(office._id, 'endDate', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className=''>
                                                            <div className="flex items-center gap-2 duration-300">
                                                                <Radio
                                                                    disabled={office.isPartiallyAssigned}
                                                                    checked={formData.selectedOffices.find(o => o.id === office._id)?.isFullTime || false}
                                                                    onChange={(e) => handleOfficeDetailChange(office._id, 'isFullTime', e.target.checked)}
                                                                    label="Full Time"
                                                                    className="h-4 w-4 m-0"
                                                                />
                                                                <Radio
                                                                    checked={!formData.selectedOffices.find(o => o.id === office._id)?.isFullTime || false}
                                                                    onChange={(e) => handleOfficeDetailChange(office._id, 'isFullTime', !e.target.checked)}
                                                                    label="Part Time"
                                                                    className="h-4 w-4 m-0"
                                                                />
                                                            </div>
                                                            <div className="w-full flex items-center gap-2 col-span-2 animate-in">
                                                                {
                                                                    !formData.selectedOffices.find(o => o.id === office._id)?.isFullTime && (
                                                                        <div className="w-full border rounded-lg p-2">
                                                                            <span className="text-gray-600 text-sm font-bold">Daily Slot</span>
                                                                            <hr className="my-1" />
                                                                            <div className="flex justify-between items-center gap-2">
                                                                                <div className="text-gray-600 text-sm">From</div>
                                                                                <select
                                                                                    value={formData.selectedOffices.find(o => o.id === office._id)?.startTime || ''}
                                                                                    onChange={(e) => handleOfficeDetailChange(office._id, 'startTime', e.target.value)}
                                                                                >
                                                                                    {
                                                                                        HOURLY_TIME_SLOTS_ENUM.map((pair) => (
                                                                                            <option key={pair.key} value={pair.key}>{pair.value}</option>
                                                                                        ))
                                                                                    }
                                                                                </select>
                                                                            </div>
                                                                            <div className="flex justify-between items-center gap-2">
                                                                                <div className="text-gray-600 text-sm">To</div>
                                                                                <select
                                                                                    value={formData.selectedOffices.find(o => o.id === office._id)?.endTime || ''}
                                                                                    onChange={(e) => handleOfficeDetailChange(office._id, 'endTime', e.target.value)}
                                                                                >
                                                                                    {
                                                                                        HOURLY_TIME_SLOTS_ENUM.map((pair) => (
                                                                                            <option key={pair.key} value={pair.key}>{pair.value}</option>
                                                                                        ))
                                                                                    }
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>
                                                        </div>
                                                        <div className='border rounded-lg p-2'>
                                                            <div className="text-gray-600 text-sm font-bold">Bookings</div>
                                                            <hr className="my-1" />
                                                            <div className='h-[100px] overflow-y-auto'>
                                                                {
                                                                    office.bookings?.slice(0, 10).map((booking, index) => (
                                                                        <div key={index} className='flex justify-between items-center'>
                                                                            <div className='text-xs'>
                                                                                {booking.dailyStartTime ?
                                                                                    `${moment(booking.dailyStartTime).format('hh:mm a')} - ${moment(booking.dailyEndTime).format('hh:mm a')}` :
                                                                                    'Full Time'}
                                                                                <div className='text-xs'>
                                                                                    {booking.endDate ? moment(booking.endDate).format('ll') : 'Open Ended'}
                                                                                </div>
                                                                            </div>
                                                                            <div className='text-xs'>
                                                                                <Link to={`/customer/${booking?.contract?.user?._id}`} className='flex items-center gap-1 text-blue-600 hover:underline duration-300' target='_blank'>
                                                                                    <ExternalLinkIcon className='w-3 h-3' />{booking?.contract?.user?.name}
                                                                                </Link>
                                                                            </div>
                                                                        </div>
                                                                    )) || <div className="text-gray-600 text-sm">No bookings yet</div>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Fragment>
                                    )
                                })}
                        </List>
                    )
                }
            </div>
            <hr className='my-4' />
            <div className="flex justify-end gap-2">
                <Button onClick={handlePrev} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    {availableTabs[activeStep - 1].label}<ArrowLeftIcon className='w-4 h-4' /> Previous Step
                </Button>
                <Button disabled={checkSelectedOfficesSpecifiedRent.length > 0} onClick={handleNext} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    Next Step <ArrowRightIcon className='w-4 h-4' /> {availableTabs[activeStep + 1].label}
                </Button>
            </div>
        </div>
    )

    const renderSeatSelection = () => (
        <div className="mt-4 min-h-[480px]">
            <Input
                label="Search Seats"
                icon={<SearchIcon className="h-4 w-4" />}
                value={searchTerm}
                disabled={isLoadingSeats}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="h-[390px] overflow-y-auto">
                {
                    isLoadingSeats ? (
                        <div className="flex justify-center items-center h-full">
                            <Spinner className="w-4 h-4" />
                        </div>
                    ) : (
                        <List>
                            {filteredSeats
                                .filter(seat => seat.status === OPERATIONAL_STATUSES.OPERATIONAL)
                                .slice() // Create a copy to avoid mutating the original array
                                .map((seat) => {
                                    return (
                                        <Fragment key={seat._id}>
                                            <ListItem
                                                className="py-1"
                                                // disabled={availability === 0}
                                                selected={formData.selectedSeats.some(s => s.id === seat._id)}
                                                onClick={() => handleSeatSelection(seat._id)}>
                                                <div className="flex w-full items-center">
                                                    <div className="hidden md:block">
                                                        <Checkbox
                                                            checked={formData.selectedSeats.some(s => s.id === seat._id)}
                                                            className="h-4 w-4"
                                                            ripple={false}
                                                            readOnly
                                                        />
                                                    </div>
                                                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center w-full">
                                                        <div className="font-thin">
                                                            Seat # {seat.seatCode} ({seat.seatType})<br />
                                                            <span className="text-xs">{seat.ratePerMonth} Per Month - {convertToPresentableText(seat.seatType)} - {convertToPresentableText(seat.deskType)} Desk</span>
                                                        </div>
                                                        {/* <div className="font-thin text-xs text-gray-600 text-end">
                                                            {availability === 0 ? "Not Available" : `Available ${availability}h`} <br />
                                                            {getOrdinal(seat.coworking.floor.number)} Floor in {" "}
                                                            {seat.coworking.floor.building.name}, {seat.coworking.floor.building.branch.name} Branch
                                                        </div> */}
                                                    </div>
                                                </div>
                                            </ListItem>
                                            {formData.selectedSeats.some(o => o.id === seat._id) && (
                                                <div className="border-l-2 border-gray-400 ml-2 pl-2">
                                                    <div className={`grid grid-cols-1 md:grid-cols-4 gap-2 mb-2`}>
                                                        <div className="w-full">
                                                            <Input
                                                                type="number"
                                                                label="Monthly Rent"
                                                                placeholder={seat.ratePerMonth}
                                                                value={formData.selectedSeats.find(o => o.id === seat._id)?.monthlyRent || ''}
                                                                onChange={(e) => handleSeatDetailChange(seat._id, 'monthlyRent', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="w-full col-span-3">
                                                            <Input
                                                                type="text"
                                                                label="Additional Comments"
                                                                value={formData.selectedSeats.find(o => o.id === seat._id)?.comment || ''}
                                                                onChange={(e) => handleSeatDetailChange(seat._id, 'comment', e.target.value)}
                                                            />
                                                        </div>

                                                    </div>
                                                    <div className={`grid grid-cols-1 md:grid-cols-3 gap-2 mb-2`}>
                                                        <div className={`flex flex-col md:flex-row gap-2 w-full`}>

                                                            <div className={`p-2 border rounded-lg w-full`}>
                                                                <span className="text-gray-600 text-sm font-bold">Booking Period</span>
                                                                <hr className="my-1" />
                                                                <div className={`mt-2`}>
                                                                    <Input
                                                                        type="date"
                                                                        label="Booking From"
                                                                        value={formData.selectedSeats.find(o => o.id === seat._id)?.startDate || ''}
                                                                        onChange={(e) => handleSeatDetailChange(seat._id, 'startDate', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className={`mt-2`}>
                                                                    <Input
                                                                        type="date"
                                                                        label="Booking Till"
                                                                        value={formData.selectedSeats.find(o => o.id === seat._id)?.endDate || ''}
                                                                        onChange={(e) => handleSeatDetailChange(seat._id, 'endDate', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className=''>
                                                            <div className="flex items-center gap-2 duration-300">
                                                                <Radio
                                                                    disabled={seat.isPartiallyAssigned}
                                                                    checked={formData.selectedSeats.find(o => o.id === seat._id)?.isFullTime || false}
                                                                    onChange={(e) => handleSeatDetailChange(seat._id, 'isFullTime', e.target.checked)}
                                                                    label="Full Time"
                                                                    className="h-4 w-4 m-0"
                                                                />
                                                                <Radio
                                                                    checked={!formData.selectedSeats.find(o => o.id === seat._id)?.isFullTime || false}
                                                                    onChange={(e) => handleSeatDetailChange(seat._id, 'isFullTime', !e.target.checked)}
                                                                    label="Part Time"
                                                                    className="h-4 w-4 m-0"
                                                                />
                                                            </div>
                                                            <div className="w-full flex items-center gap-2 col-span-2 animate-in">
                                                                {
                                                                    !formData.selectedSeats.find(o => o.id === seat._id)?.isFullTime && (
                                                                        <div className="w-full border rounded-lg p-2">
                                                                            <span className="text-gray-600 text-sm font-bold">Daily Slot</span>
                                                                            <hr className="my-1" />
                                                                            <div className="flex justify-between items-center gap-2">
                                                                                <div className="text-gray-600 text-sm">From</div>
                                                                                <select
                                                                                    value={formData.selectedSeats.find(o => o.id === seat._id)?.startTime || ''}
                                                                                    onChange={(e) => handleSeatDetailChange(seat._id, 'startTime', e.target.value)}
                                                                                >
                                                                                    {
                                                                                        HOURLY_TIME_SLOTS_ENUM.map((pair) => (
                                                                                            <option key={pair.key} value={pair.key}>{pair.value}</option>
                                                                                        ))
                                                                                    }
                                                                                </select>
                                                                            </div>
                                                                            <div className="flex justify-between items-center gap-2">
                                                                                <div className="text-gray-600 text-sm">To</div>
                                                                                <select
                                                                                    value={formData.selectedSeats.find(o => o.id === seat._id)?.endTime || ''}
                                                                                    onChange={(e) => handleSeatDetailChange(seat._id, 'endTime', e.target.value)}
                                                                                >
                                                                                    {
                                                                                        HOURLY_TIME_SLOTS_ENUM.map((pair) => (
                                                                                            <option key={pair.key} value={pair.key}>{pair.value}</option>
                                                                                        ))
                                                                                    }
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>
                                                        </div>
                                                        <div className='border rounded-lg p-2'>
                                                            <div className="text-gray-600 text-sm font-bold">Active Bookings</div>
                                                            <hr className="my-1" />
                                                            <div className='h-[100px] overflow-y-auto'>
                                                                {
                                                                    seat.bookings?.slice(0, 10).map((booking, index) => (
                                                                        <div key={index} className='flex justify-between items-center'>
                                                                            <div className='text-xs text-gray-600'>
                                                                                {booking.dailyStartTime ?
                                                                                    `${moment(booking.dailyStartTime).format('hh:mm a')} - ${moment(booking.dailyEndTime).format('hh:mm a')}` :
                                                                                    'Full Time'}
                                                                                <div className='text-xs'>
                                                                                    {booking.endDate ? moment(booking.endDate).format('ll') : 'Open Ended'}
                                                                                </div>
                                                                            </div>
                                                                            <div className='text-xs'>
                                                                                <Link to={`/customer/${booking.contract.user._id}`} className='flex items-center gap-1 text-blue-600 hover:underline duration-300' target='_blank'>
                                                                                    <ExternalLinkIcon className='w-3 h-3' />{booking.contract.user.name}
                                                                                </Link>
                                                                            </div>
                                                                        </div>
                                                                    )) || <div className="text-gray-600 text-sm">No bookings yet</div>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Fragment>
                                    )
                                }
                                )}
                        </List>
                    )
                }
            </div>
            <hr className='my-4' />
            <div className="flex justify-end gap-2">
                <Button onClick={handlePrev} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    {availableTabs[activeStep - 1].label}<ArrowLeftIcon className='w-4 h-4' /> Previous Step
                </Button>
                <Button disabled={(!formData.selectedOffices.length && !formData.selectedSeats.length) || checkSelectedSeatsSpecifiedRent.length > 0} onClick={handleNext} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    Next Step <ArrowRightIcon className='w-4 h-4' /> {availableTabs[activeStep + 1].label}
                </Button>
            </div>
        </div>
    )

    const handleRemoveOneTimeOtherCharge = (index) => {
        const charges = [...formData.oneTimeOtherCharges];
        charges.splice(index, 1);
        setFormData({
            ...formData,
            oneTimeOtherCharges: charges
        });
    };

    const handleAddOneTimeOtherCharge = () => {
        setFormData({
            ...formData,
            oneTimeOtherCharges: [
                { name: '', description: '', amount: 0, note: '', dueDate: '' },
                ...formData.oneTimeOtherCharges,
            ]
        });
    };

    const handleInputChangeForOneTimeOtherCharge = (index, e) => {
        const { name, value } = e.target;
        console.log('name', name);
        console.log('value', value);
        const charges = [...formData.oneTimeOtherCharges];
        charges[index][name] = value;
        setFormData({
            ...formData,
            oneTimeOtherCharges: charges
        });
    };

    const renderPaymentSelection = () => (
        <div className='space-y-2'>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className='w-full'>
                    <Input
                        label="Advance Amount"
                        name="totalAdvanceRent"
                        required
                        type="number"
                        value={formData.totalAdvanceRent}
                        onChange={handleInputChange}
                    />
                </div>
                <div className='w-full'>
                    <Checkbox
                        label="Is Advance Amount already paid?"
                        name="isAdvanceAmountPaid"
                        checked={formData.isAdvanceAmountPaid}
                        onChange={handleCheckboxChange}
                    />
                </div>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                <div className='w-full'>
                    <Input
                        label="Security Deposit"
                        name="securityDeposit"
                        type="number"
                        value={formData.securityDeposit}
                        onChange={handleInputChange}
                    />
                </div>
                <div className='w-full'>
                    <Input
                        label="One Time Service Charges"
                        name="oneTimeServiceCharges"
                        type="number"
                        value={formData.oneTimeServiceCharges}
                        onChange={handleInputChange}
                    />
                </div>
            </div>
            <div className='space-y-2 border border-blue-gray-200 rounded-lg p-2'>
                <div className='flex justify-between items-center'>
                    <div className='text-sm text-gray-600'>
                        Any Custom Charges:
                    </div>
                    <div className='text-sm text-gray-600'>
                        <Button onClick={handleAddOneTimeOtherCharge} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                            <PlusIcon className='w-4 h-4' /> Add
                        </Button>
                    </div>
                </div>
                <hr className='my-1' />
                <div className='space-y-2 h-[270px] overflow-y-auto '>
                    {
                        formData.oneTimeOtherCharges?.length > 0 ? (
                            <>
                                {formData.oneTimeOtherCharges?.map((charge, i) => (
                                    <div key={i} className='grid grid-cols-1 md:grid-cols-4 gap-2 mt-1'>
                                        <div className=''>
                                            <Input
                                                label={'Purpose'}
                                                name="name"
                                                value={charge.name}
                                                onChange={(e) => handleInputChangeForOneTimeOtherCharge(i, e)}
                                            />
                                        </div>
                                        <div className=''>
                                            <Input
                                                label={'Description'}
                                                name="description"
                                                type="text"
                                                value={charge.description}
                                                onChange={(e) => handleInputChangeForOneTimeOtherCharge(i, e)}
                                            />
                                        </div>
                                        <div className=''>
                                            <Input
                                                label={'Amount'}
                                                name="amount"
                                                type="number"
                                                value={charge.amount}
                                                onChange={(e) => handleInputChangeForOneTimeOtherCharge(i, e)}
                                            />
                                        </div>
                                        {/* <div className=''>
                                <Input
                                    label={'Note'}
                                    name={charge.note}
                                    type="text"
                                    value={charge.note}
                                    onChange={(e) => handleInputChangeForOneTimeOtherCharge(i, e)}
                                />
                            </div> */}
                                        <div className='flex items-center  gap-2'>
                                            <Input
                                                label={'Due Date'}
                                                name="dueDate"
                                                type="date"
                                                value={charge.dueDate}
                                                onChange={(e) => handleInputChangeForOneTimeOtherCharge(i, e)}
                                            />
                                            <IconButton onClick={() => handleRemoveOneTimeOtherCharge(i)} size='sm' variant='outlined' className='rounded-full'>
                                                <TrashIcon className='w-4 h-4' />
                                            </IconButton>
                                        </div>
                                    </div>
                                ))}
                            </>
                        ) : (
                            <div className='text-gray-600 text-sm text-center py-2'>No custom charges added</div>
                        )
                    }
                </div>
            </div>
            <hr className='my-4' />
            <div className="flex justify-end gap-2">
                <Button onClick={handlePrev} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    {availableTabs[activeStep - 1].label}<ArrowLeftIcon className='w-4 h-4' /> Previous Step
                </Button>
                <Button onClick={handleNext} disabled={!formData.totalAdvanceRent} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    Next Step <ArrowRightIcon className='w-4 h-4' /> {availableTabs[activeStep + 1].label}
                </Button>
            </div>
        </div>
    )

    const renderSummary = () => (
        <div className="space-y-4">
            <div className="space-y-2">
                {
                    formData.contractType !== CONNECTION_TYPES.DAY_PASS ? (

                        <div className={`flex overflow-x-auto snap-x snap-mandatory pe-4`}>
                            {
                                formData.selectedOffices.length === 0 ? <></> : (
                                    <div className='flex flex-col items-center justify-center gap-2 border p-1 flex-shrink-0 snap-center border-blue-100 border-t-8 border-r-1 border-l-8 border-b-1  bg-blue-50'>
                                        <div className='text-sm text-blue-500'>Offices</div>
                                        <div className='text-2xl font-bold text-blue-500'>{formData.selectedOffices.length}</div>
                                    </div>
                                )
                            }
                            {
                                formData.selectedOffices.map(office => (
                                    <div key={office._id} className='flex-shrink-0'>
                                        <div className='border border-4 border-blue-100'></div>
                                        <div className="border rounded-lg p-1 flex-shrink-0 snap-center border-blue-100 mt-1 mx-1">
                                            <table className='w-full table-auto'>
                                                <thead>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm border-b">
                                                        <th colSpan={2} className='py-1 px-2'>Office: {office.name}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Rent</td>
                                                        <td className='font-thin py-1 px-2'>PKR {formatNumberWithCommas(office.monthlyRent || 0)} </td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>From</td>
                                                        <td className='font-thin py-1 px-2'>{office.startDate || formData.startDate || <span className='text-red-500'>Not set</span>}</td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Till</td>
                                                        <td className='font-thin py-1 px-2'>{office?.endDate || 'Not set'}</td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Comments</td>
                                                        <td className='font-thin py-1 px-2'>{office.comment}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))
                            }
                            {
                                formData.selectedSeats.length === 0 ? <></> : (
                                    <div className='text-center flex flex-col items-center justify-center gap-2 border p-1 flex-shrink-0 snap-center border-pink-100 border-t-8 border-r-1 border-l-8 border-b-1  bg-pink-50 ms-1'>
                                        <div className='text-sm text-pink-500'>Seats</div>
                                        <div className='text-2xl font-bold text-pink-500'>{formData.selectedSeats.length}</div>
                                    </div>
                                )
                            }
                            {
                                formData.selectedSeats.map(seat => (
                                    <div key={seat._id} className='flex-shrink-0'>
                                        <div className='border border-4 border-pink-100'></div>
                                        <div className="border rounded-lg p-1 flex-shrink-0 snap-center border-pink-100 mt-1 mx-1">
                                            <table className='w-full table-auto'>
                                                <thead>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm border-b">
                                                        <th colSpan={2} className='py-1 px-2 text-pink-500'>Seat: {seat.name}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Rent</td>
                                                        <td className='font-thin py-1 px-2'>{seat.monthlyRent ? `PKR ${formatNumberWithCommas(seat.monthlyRent || 0)}` : 'NA'}</td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>From</td>
                                                        <td className='font-thin py-1 px-2'>{seat.startDate || formData.startDate || <span className='text-red-500'>Not set</span>}</td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Till</td>
                                                        <td className='font-thin py-1 px-2'>{seat?.endDate || 'Not set'}</td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Comments</td>
                                                        <td className='font-thin py-1 px-2'>{seat.comment}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))
                            }
                        </div>) : (
                        <div className='border rounded-lg border-green-100 p-4 bg-green-50 grid grid-cols-1 md:grid-cols-2 gap-2'>
                            <div>
                                <table className=' table-auto'>
                                    <tbody>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Pass Type</td>
                                            <td className='font-bold py-1 px-2 text-green-500'>{convertToPresentableText(formData.dayPass.passType)}</td>
                                        </tr>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Booking Required</td>
                                            <td className='font-thin py-1 px-2'>{formData.dayPass.bookingRequired ? 'Yes' : 'No'}</td>
                                        </tr>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Per Slot Hours</td>
                                            <td className='font-thin py-1 px-2'>{formData.dayPass.perSlotCapacity}</td>
                                        </tr>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Daily Available Hours</td>
                                            <td className='font-thin py-1 px-2'>{formData.dayPass.dailyCapacity}</td>
                                        </tr>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Includes Meeting Room</td>
                                            <td className='font-thin py-1 px-2'>{formData.dayPass.includesMeetingRoom ? 'Yes' : 'No'}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div>
                                <table className='table-auto'>
                                    <tbody>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Max Days Per Month</td>
                                            <td className='font-thin py-1 px-2'>{formData.dayPass.maxDaysPerMonth}</td>
                                        </tr>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Rent</td>
                                            <td className='font-thin py-1 px-2'>PKR {formData.dayPass.totalPrice || <span className='text-red-500'>0</span>}/{formData.dayPass.priceType}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                    <div className='border rounded-lg'>
                        <table className='w-full table-auto'>
                            <tbody>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Customer</td>
                                    <td className='font-thin py-1 px-2'>
                                        {
                                            customer ?
                                                <Link to={`/customer/${customer._id}`} className='text-blue-300 flex items-center gap-2 font-bold hover:underline' target='_blank'>
                                                    {convertToPresentableText(customer.name)} <ExternalLinkIcon className='w-4 h-4' />
                                                </Link>
                                                :
                                                formData?.user ?
                                                    <Link to={`/user/${formData?.user?._id}`} className='text-blue-300 flex items-center gap-2 font-bold hover:underline' target='_blank'>
                                                        {convertToPresentableText(formData.user.name)} <ExternalLinkIcon className='w-4 h-4' />
                                                    </Link>
                                                    :
                                                    <span className='text-red-500'>Not Selected</span>
                                        }
                                    </td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Type</td>
                                    <td className='font-thin py-1 px-2'>{convertToPresentableText(formData.contractType)}</td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Notice Period</td>
                                    <td className='font-thin py-1 px-2'>{formData.noticePeriod} Weeks</td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Duration</td>
                                    <td className='font-thin py-1 px-2'>{moment(formData.startDate).format('ll') || <span className='text-red-500'>Not set</span>} - {formData.endDate ? moment(formData.endDate).format('ll') : <span className='text-gray-700 text-xs'>Open (with {formData.noticePeriod} Weeks Notice)</span>}</td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Purpose / Reason</td>
                                    <td className='font-thin py-1 px-2'>{formData.reason}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className='border rounded-lg'>
                        <table className='table-auto w-full'>
                            <thead>
                                <tr className="even:bg-blue-gray-50/50 text-sm border-b">
                                    <th className='py-1 px-2 text-left'>Summary</th>
                                    <th className='py-1 px-2 text-left'>Amount</th>
                                    <th className='py-1 px-2 text-left'>Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Advance Rent</td>
                                    <td className='font-thin py-1 px-2'>
                                        {summary.advanceRent ?
                                            `PKR ${formatNumberWithCommas(summary.advanceRent)}` :
                                            <span className='text-red-500 text-sm'>(Please specify Rent)</span>}
                                    </td>
                                    <td className='font-thin py-1 px-2'>
                                        {summary.exceptedAdvanceRent ?
                                            `PKR ${formatNumberWithCommas(summary.exceptedAdvanceRent)} (2 x Rent)`
                                            : <span className='text-red-500 text-sm'>N/A</span>}
                                    </td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Rent</td>
                                    <td className='font-thin py-1 px-2'>{summary.totalRent ? `PKR ${formatNumberWithCommas(summary.totalRent)}/${summary.rentType}` : <span className='text-red-500 text-sm'>(Please select Offices or Seats)</span>}</td>
                                    <td className='font-thin py-1 px-2'>{summary.totalRent ? `PKR ${formatNumberWithCommas(summary.totalRent)}/${summary.rentType}` : <span className='text-red-500 text-sm'>(Please select Offices or Seats)</span>}</td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Security Deposit</td>
                                    <td className='font-thin py-1 px-2'>{summary.securityDeposit ? `PKR ${formatNumberWithCommas(summary.securityDeposit)}` : '—'}</td>
                                    <td className='font-thin py-1 px-2'>—</td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Service Charges</td>
                                    <td className='font-thin py-1 px-2'>{summary.oneTimeServiceCharges ? `PKR ${formatNumberWithCommas(summary.oneTimeServiceCharges)}` : '—'}</td>
                                    <td className='font-thin py-1 px-2'>—</td>
                                </tr>
                                {(summary.oneTimeOtherCharges?.length > 0) && (
                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                        <td className='font-bold py-1 px-2'>Custom Charges</td>
                                        <td className='font-thin py-1 px-2'>
                                            PKR {formatNumberWithCommas(
                                                summary.oneTimeOtherCharges.reduce((sum, c) => sum + toMoney(c.amount), 0)
                                            )}
                                        </td>
                                        <td className='font-thin py-1 px-2'>{summary.oneTimeOtherCharges.length} item(s)</td>
                                    </tr>
                                )}
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Total Payable</td>
                                    <td className='font-thin py-1 px-2'>{summary.totalPayable ? `PKR ${formatNumberWithCommas(summary.totalPayable)}` : <span className='text-red-500 text-sm'>(Please add Advance Rent and select Offices or Seats)</span>}</td>
                                    <td className='font-thin py-1 px-2'>All charges included</td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>1st Month</td>
                                    <td className='font-thin py-1 px-2'>{summary.firstMonthPayable ? `PKR ${formatNumberWithCommas(summary.firstMonthPayable)}` : <span className='text-red-500 text-sm'>(Please select Offices or Seats)</span>}</td>
                                    <td className='font-thin py-1 px-2'>Due at signing</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div className={`flex justify-end gap-2 mt-2`}>
                <Button onClick={handlePrev} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    {availableTabs[activeStep - 1].label}<ArrowLeftIcon className='w-4 h-4' /> Previous Step
                </Button>
                <Button type="submit" size='sm' className="rounded-full flex items-center gap-2" disabled={loadingAddContract}>
                    {loadingAddContract && <Spinner className="w-4 h-4" />} {isUpdating ? "Update" : "Create Contract"}
                </Button>
            </div>
        </div>
    )


    return (
        <ModalX
            isOpen={isOpen}
            onClose={onClose}
            title={isUpdating ? "Update Contract" : "Add New Contract"}
            maxWidth="xxxl"
        >
            <form onSubmit={handleFormSubmit}>
                <div className="w-full px-1 md:px-10 lg:px-24 py-2">
                    <Stepper
                        activeStep={activeStep}
                        isLastStep={(value) => setIsLastStep(value)}
                        isFirstStep={(value) => setIsFirstStep(value)}
                    >
                        {
                            availableTabs.map((tab, index) => (
                                <Step
                                    key={tab.value}
                                    label={tab.label}
                                // onClick={() => setActiveStep(index)}
                                // disabled={tab.value === CONNECTION_TYPES.DAY_PASS && checkSelectedSeatsSpecifiedRent.length > 0}
                                >
                                    {tab.icon}
                                    <div className="absolute -bottom-[2.5rem] w-max text-center hidden md:block">
                                        <h6 className={`text-sm font-medium ${activeStep === index ? 'text-blue-500' : 'text-gray-500'} flex items-center justify-center gap-2`}>
                                            <span className="hidden xl:block">Step {index + 1}</span>
                                        </h6>
                                        <h6 className={`hidden xl:block text-xs font-normal ${activeStep === index ? 'text-blue-500' : 'text-gray-500'}`}>
                                            {tab.description}
                                        </h6>
                                    </div>
                                </Step>
                            ))
                        }
                    </Stepper>
                </div>
                <hr className="mt-2 md:mt-[3.5rem]" />
                <div className="w-full text-center my-4 xl:hidden ">
                    {availableTabs[activeStep].description}
                </div>
                <div className="min-h-[480px] mt-4">
                    {activeStep === 0 && renderCustomerSelection()}
                    {activeStep === 1 && renderGeneralDetails()}
                    {activeStep === 2 && renderOfficeSelection()}
                    {activeStep === 3 && renderSeatSelection()}
                    {activeStep === 4 && renderPaymentSelection()}
                    {activeStep === 5 && renderSummary()}
                </div>
            </form >
        </ModalX >
    )
}
export default ContractFormModal;